import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FolderOpen,
  Search,
  X as XIcon,
  Users,
  DollarSign,
  FileText,
  Boxes,
  Briefcase,
} from 'lucide-react';
import FormDrawer, { FormSection } from './FormDrawer';

export const PROJECT_KIND_VALUES = ['project', 'product'];

const emptyForm = {
  name: '',
  description: '',
  client: '',
  is_billable: true,
  hourly_rate: '',
  kind: 'project',
  member_ids: [],
};

const normalizeKind = (value) =>
  PROJECT_KIND_VALUES.includes(value) ? value : 'project';

/**
 * ProjectFormDrawer
 *
 * Self-contained drawer to create or edit a project. Owns its own form
 * state. Parent only needs to pass `open`, `onClose`, `editingProject`
 * (or null), the team list, and an async `onSubmit(projectData)` that
 * performs the mutation.
 */
const ProjectFormDrawer = ({
  open,
  onClose,
  editingProject,
  teamMembers = [],
  currentUser,
  onSubmit,
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialise / reset form whenever the drawer opens
  useEffect(() => {
    if (!open) return;
    if (editingProject) {
      setFormData({
        name: editingProject.name || '',
        description: editingProject.description || '',
        client: editingProject.client || '',
        is_billable: editingProject.is_billable ?? true,
        hourly_rate: editingProject.hourly_rate ?? '',
        kind: normalizeKind(editingProject.kind),
        member_ids: editingProject.member_ids || [],
      });
    } else {
      setFormData(emptyForm);
    }
    setMemberSearchTerm('');
  }, [open, editingProject]);

  const handleMemberToggle = (memberId) => {
    setFormData((prev) => ({
      ...prev,
      member_ids: prev.member_ids.includes(memberId)
        ? prev.member_ids.filter((id) => id !== memberId)
        : [...prev.member_ids, memberId],
    }));
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isSubmitting) return;

    if (!formData.name || formData.name.trim() === '') {
      toast.error('Please enter a project name');
      return;
    }
    if (!formData.description || formData.description.trim() === '') {
      toast.error('Please enter a project description');
      return;
    }
    if (!formData.member_ids || formData.member_ids.length === 0) {
      toast.error('Please assign at least one team member');
      return;
    }

    setIsSubmitting(true);
    try {
      const projectData = {
        ...formData,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        kind: normalizeKind(formData.kind),
      };
      await onSubmit(projectData);
      onClose?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredMembers = useMemo(() => {
    const search = memberSearchTerm.trim().toLowerCase();
    return teamMembers
      .filter((member) => {
        if (currentUser?.role === 'admin') return true;
        return member.role !== 'admin';
      })
      .filter((member) => {
        if (!search) return true;
        return (
          (member.full_name && member.full_name.toLowerCase().includes(search)) ||
          (member.username && member.username.toLowerCase().includes(search)) ||
          (member.email && member.email.toLowerCase().includes(search)) ||
          (member.role && member.role.toLowerCase().includes(search))
        );
      });
  }, [teamMembers, memberSearchTerm, currentUser]);

  const selectedCount = formData.member_ids.length;

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        className="btn-secondary"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting}
        className="btn-primary min-w-[140px] justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            {editingProject ? 'Updating…' : 'Creating…'}
          </span>
        ) : editingProject ? (
          'Update Project'
        ) : (
          'Create Project'
        )}
      </button>
    </>
  );

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title={editingProject ? 'Edit Project' : 'Create New Project'}
      subtitle={
        editingProject
          ? 'Update project details, billing, and team.'
          : 'Set up a project with billing and team members.'
      }
      icon={<FolderOpen className="h-5 w-5" />}
      iconColor="primary"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="space-y-0">
        <FormSection
          title="Details"
          description="Basic information about this project."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. Website Redesign"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                Client
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. Acme Inc."
                value={formData.client}
                onChange={(e) => setFormData({ ...formData, client: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
              Type <span className="text-red-500">*</span>
            </label>
            <div
              role="radiogroup"
              aria-label="Project type"
              className="inline-flex rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800 p-1"
              data-testid="project-kind-toggle"
            >
              {[
                { value: 'project', label: 'Project', icon: Briefcase },
                { value: 'product', label: 'Product', icon: Boxes },
              ].map(({ value, label, icon: Icon }) => {
                const active = formData.kind === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setFormData({ ...formData, kind: value })}
                    data-testid={`project-kind-${value}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      active
                        ? 'bg-white dark:bg-secondary-700 text-primary-700 dark:text-primary-300 shadow-sm'
                        : 'text-secondary-600 dark:text-secondary-300 hover:text-secondary-900 dark:hover:text-white'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
              Products are long-lived initiatives; projects are time-bound
              engagements. This filters the Resource Utilization view.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
              <span className="inline-flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                Description <span className="text-red-500">*</span>
              </span>
            </label>
            <textarea
              className="input-field resize-none"
              rows="3"
              placeholder="What is this project about?"
              value={formData.description}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 250) {
                  setFormData({ ...formData, description: value });
                }
              }}
              maxLength={250}
              required
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                <span className="font-semibold text-secondary-700 dark:text-secondary-300">
                  {formData.description ? formData.description.length : 0}
                </span>
                /250
              </span>
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                Max 250 characters
              </span>
            </div>
          </div>
        </FormSection>

        <FormSection
          title="Billing"
          description="Set whether this project is billable and its hourly rate."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                Billable
              </label>
              <label className="flex items-center justify-between gap-3 h-[42px] px-3 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 cursor-pointer hover:border-primary-400 transition-colors">
                <span className="flex items-center gap-2 text-sm font-medium text-secondary-700 dark:text-secondary-200">
                  <DollarSign className="h-4 w-4 text-primary-500" />
                  {formData.is_billable ? 'On' : 'Off'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.is_billable}
                  onClick={() =>
                    setFormData({ ...formData, is_billable: !formData.is_billable })
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-900 ${
                    formData.is_billable
                      ? 'bg-primary-600'
                      : 'bg-secondary-300 dark:bg-secondary-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      formData.is_billable ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                Hourly Rate
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-500 dark:text-secondary-400 text-sm">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input-field pl-7 h-[42px]"
                  placeholder="0.00"
                  value={formData.hourly_rate}
                  onChange={(e) =>
                    setFormData({ ...formData, hourly_rate: e.target.value })
                  }
                  disabled={!formData.is_billable}
                />
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection
          title="Team Members"
          description="Select everyone who should have access to this project."
        >
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  Assignees <span className="text-red-500">*</span>
                </span>
              </label>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  selectedCount > 0
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
                    : 'bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400'
                }`}
              >
                {selectedCount} selected
              </span>
            </div>

            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search team members..."
                className="input-field pl-9 pr-9"
                value={memberSearchTerm}
                onChange={(e) => setMemberSearchTerm(e.target.value)}
              />
              {memberSearchTerm && (
                <button
                  type="button"
                  onClick={() => setMemberSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200"
                  aria-label="Clear search"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 divide-y divide-secondary-100 dark:divide-secondary-700">
              {teamMembers.length === 0 ? (
                <p className="p-4 text-sm text-secondary-500 dark:text-secondary-400">
                  No team members available.
                </p>
              ) : filteredMembers.length === 0 ? (
                <p className="p-4 text-sm text-secondary-500 dark:text-secondary-400">
                  No members found matching "{memberSearchTerm}".
                </p>
              ) : (
                filteredMembers.map((member) => {
                  const checked = formData.member_ids.includes(member.id);
                  return (
                    <label
                      key={member.id}
                      className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                        checked
                          ? 'bg-primary-50/60 dark:bg-primary-900/20'
                          : 'hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleMemberToggle(member.id)}
                        className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-700"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                          {member.full_name || member.username}
                          {member.id === currentUser?.id && (
                            <span className="ml-1.5 text-xs font-normal text-primary-600 dark:text-primary-300">
                              (You)
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                          {member.email}
                          {member.role && ` • ${member.role}`}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            {selectedCount === 0 && (
              <p className="text-xs text-red-500 mt-2">
                At least one team member must be assigned.
              </p>
            )}
          </div>
        </FormSection>
      </form>
    </FormDrawer>
  );
};

export default ProjectFormDrawer;
